import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/lib/content";
import { Header } from "@/components/Header";
import { Marquee } from "@/components/Marquee";
import { WaitlistForm } from "@/components/WaitlistForm";
import { WingmanIcon } from "@/components/WingmanIcon";
import { CrossfadeCarousel } from "@/components/CrossfadeCarousel";

const ease = [0.16, 1, 0.3, 1];

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease, delay }}
  >
    {children}
  </motion.div>
);

export default function Landing() {
  const { t } = useLang();
  const heroRef = useRef(null);
  const formRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div id="top" className="relative bg-[#0A0A0A]">
      <Header onJoin={scrollToForm} />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen w-full overflow-hidden">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-6 md:px-12 pt-24">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mb-8 text-[11px] md:text-xs uppercase tracking-[0.35em] text-[#D4AF37] font-sans-body"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-serif-display font-light leading-[0.95] text-[#FAFAFA] text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-4xl">
            <MaskLine delay={0.35}>{t.hero.line1}</MaskLine>
            <MaskLine delay={0.45}>{t.hero.line2}</MaskLine>
            <MaskLine delay={0.55}>{t.hero.line3}</MaskLine>
            <MaskLine delay={0.65}>
              <span className="italic text-[#D4AF37]">{t.hero.line3accent}</span>
            </MaskLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 1 }}
            className="mt-14"
          >
            <button
              data-testid="hero-cta-btn"
              onClick={scrollToForm}
              className="group inline-flex items-center gap-4 border border-[#D4AF37]/40 px-8 py-4 text-xs uppercase tracking-[0.2em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-black"
            >
              {t.hero.cta}
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="absolute bottom-8 left-6 md:left-12 z-10 flex items-center gap-3 text-zinc-500"
        >
          <ArrowDown className="h-4 w-4 animate-bounce" />
          <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        </motion.div>
      </section>

      {/* PROBLEM / MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-16 font-sans-body">{t.problem.label}</p>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            {t.problem.chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="grid grid-cols-12 gap-4 border-t border-white/10 py-10">
                  <span className="col-span-2 font-serif-display italic text-2xl md:text-3xl text-[#D4AF37]/70">{c.n}</span>
                  <p className="col-span-10 font-serif-display font-light text-3xl md:text-5xl leading-[1.1] text-[#FAFAFA]">
                    {c.text}
                  </p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.1}>
              <div className="border-t border-white/10 py-10">
                <p className="font-serif-display italic text-3xl md:text-5xl text-zinc-400">{t.problem.closing}</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-4 hidden lg:block">
            <Reveal delay={0.2}>
              <CrossfadeCarousel images={IMAGES.problemCarousel} />
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />
      <Marquee variant="roles" reverse />

      {/* BENEFITS */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-20 font-sans-body">{t.benefits.label}</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16">
          {t.benefits.items.map((b, i) => (
            <Reveal key={b.n} delay={i * 0.12}>
              <div className="group">
                <span className="font-serif-display italic text-xl text-[#D4AF37]/60">{b.n}</span>
                <div className="mt-6 h-px w-full bg-white/10 group-hover:bg-[#D4AF37]/50 transition-colors duration-500" />
                <h3 className="mt-8 font-serif-display font-light text-3xl md:text-4xl text-[#FAFAFA]">{b.title}</h3>
                <p className="mt-5 font-sans-body text-base leading-relaxed text-zinc-400">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INSTITUTIONAL MILESTONE (inserted between Benefits and Final CTA) */}
      <section data-testid="institutional-milestone-section" className="border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-16">
            {/* LEFT — momentum */}
            <Reveal>
              <div className="lg:pr-16 lg:border-r border-white/10">
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-sans-body">{t.milestone.leftLabel}</p>
                <h3 className="mt-6 font-serif-display font-light text-4xl md:text-5xl leading-[1.05] text-[#FAFAFA]">{t.milestone.leftHeading}</h3>
                <p className="mt-6 max-w-md font-sans-body text-base leading-relaxed text-zinc-400">{t.milestone.leftBody}</p>
                <div data-testid="investor-access-status" className="mt-10 pt-8 border-t border-white/10">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-sans-body">{t.milestone.investorLabel}</p>
                  <p className="mt-2 font-serif-display italic text-2xl md:text-3xl text-[#D4AF37]">{t.milestone.investorStatus}</p>
                </div>
              </div>
            </Reveal>
            {/* RIGHT — validation */}
            <Reveal delay={0.12}>
              <div className="lg:pl-4">
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-sans-body">{t.milestone.rightLabel}</p>
                <h3 className="mt-6 font-serif-display font-light text-4xl md:text-5xl leading-[1.05] text-[#FAFAFA]">{t.milestone.rightHeading}</h3>
                <p className="mt-6 font-serif-display italic text-2xl md:text-3xl text-zinc-300">
                  {t.milestone.partnership.split("SHOPLINE")[0]}
                  <a
                    href="https://www.shopline.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="shopline-link"
                    className="text-zinc-300 underline decoration-white/20 underline-offset-4 transition-colors duration-300 hover:text-[#D4AF37] hover:decoration-[#D4AF37]"
                  >
                    SHOPLINE
                  </a>
                  {t.milestone.partnership.split("SHOPLINE")[1] || ""}
                </p>
                <p className="mt-6 max-w-md font-sans-body text-base leading-relaxed text-zinc-400">{t.milestone.rightBody}</p>
              </div>
            </Reveal>
          </div>
          <p data-testid="shopline-trademark" className="mt-16 text-[11px] tracking-wide text-zinc-600 font-sans-body">{t.milestone.trademark}</p>
        </div>
      </section>

      {/* FINAL CTA + FORM */}
      <section ref={formRef} className="relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.finalCta} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0A0A0A]/85" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-10 font-sans-body">{t.finalCta.label}</p>
                <h2 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl leading-[0.98] text-[#FAFAFA]">
                  {t.finalCta.heading}<br />
                  <span className="italic text-[#D4AF37]">{t.finalCta.headingAccent}</span>
                </h2>
                <p className="mt-8 max-w-sm font-sans-body text-base leading-relaxed text-zinc-400">{t.finalCta.sub}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <WaitlistForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div aria-hidden="true" className="hidden md:block" />
          <div data-testid="wingman-brand" className="group flex items-center gap-3 order-last md:order-none">
            <WingmanIcon className="h-5 w-auto text-zinc-400 transition-colors duration-300 group-hover:text-[#FAFAFA]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 transition-colors duration-300 group-hover:text-zinc-300">
              {t.footer.backedBy}
            </span>
          </div>
          <div className="flex items-center gap-8 text-[11px] uppercase tracking-[0.2em] text-zinc-600">
            <span>© {new Date().getFullYear()} — {t.footer.rights}</span>
            <a href="/admin" data-testid="footer-admin-link" className="hover:text-[#D4AF37] transition-colors">{t.footer.admin}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
